import { useTranslation } from 'next-i18next'

import ResponsiveImage from '~/components/Image/ResponsiveImage'
import Button from '~/components/Buttons/Button'
import CurriculumProgressTracker from '~/curriculum/components/CurriculumProgressTracker'
import { CurriculumTopicDataType } from '~/curriculum/types/curriculum.types'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { selectShowMindDetailsOverlay, setShowMindDetailsOverlayActionCreator } from '~/redux/slices/mind-overlay'

import MindDetailsOverlay from './MindDetailsOverlay'

interface CreateMasteryTrackCardProps {
  subjectData?: CurriculumTopicDataType
  isQuizComplete: boolean
  isQuizExist: boolean
  mappingQuizApplicable: boolean
  gradeTitle?: string
  totalPrerequisites?: number
  completedPrerequisites?: number
  onClickGenerateNewPath: () => void
}

export default function CreateMasteryTrackCard({
  subjectData,
  gradeTitle = '',
  totalPrerequisites = 0,
  completedPrerequisites = 0,
  isQuizComplete,
  isQuizExist,
  mappingQuizApplicable,
  onClickGenerateNewPath,
}: CreateMasteryTrackCardProps): React.ReactElement {
  const dispatch = useAppDispatch()

  const { subjectLabel, topicId } = subjectData || { subjectLabel: '', topicId: '' }
  const showMindDetailsOverlay = useAppSelector(selectShowMindDetailsOverlay)

  const totalSubTopics = subjectData?.competencyGoals?.length || 0
  const currentProgressCount =
    subjectData?.competencyGoals?.reduce((accumulator, data) => accumulator + (data.isCompleted ? 1 : 0), 0) || 0

  const { t } = useTranslation('curriculum')

  const canReTakeQuiz = isQuizExist && mappingQuizApplicable

  return (
    <div className="relative bg-purple-darker w-full md:max-w-168 rounded md:rounded-7 shadow-lg mx-auto text-center text-white border md:border-3 border-white curriculum-pop-up-tutorial-mq-card">
      {mappingQuizApplicable && (
        <Button
          className="absolute -top-8 md:-top-10 -right-0.5 md:-right-1 lg:-right-12 flex flex-col items-center w-19 h-17 md:w-32 md:h-28 rounded-3 md:rounded-4xl bg-purple-darker border-white border md:border-3"
          onClick={() => {
            dispatch(setShowMindDetailsOverlayActionCreator(true))
          }}
        >
          <ResponsiveImage
            alt="Mind Icon"
            fit="contain"
            height={130}
            imageClassName="object-contain md:-mt-3 w-12 md:w-20 -rotate-30"
            srcKey="landing/mind-ai-v2.png"
            width={150}
          />
          <p className="text-xs md:text-sm text-white -mt-2 underline">MIND</p>
        </Button>
      )}

      <div className="flex flex-col justify-evenly items-center space-y-2 w-full h-full">
        <div className="flex mt-2 space-y-1 flex-shrink flex-col">
          <p className="text-md md:text-lg font-semibold  ">{t('myMasteryTrack')}</p>
          <p className="flex items-center justify-center space-x-1 md:text-sm text-xs">
            <span>{gradeTitle}</span>
            <span className="text-xl leading-none flex items-center justify-center">•</span>
            <span>{subjectLabel}</span>
          </p>
        </div>

        <div className={`flex gap-0.5 md:gap-3 px-2.5 ${canReTakeQuiz ? 'pb-3' : 'pb-6'}`}>
          {isQuizComplete && totalPrerequisites > 0 && (
            <>
              <CurriculumProgressTracker completedCount={completedPrerequisites} totalCount={totalPrerequisites || 0} />
              <p className="flex items-center text-lg md:text-5xl pt-7 md:pt-12">+</p>{' '}
            </>
          )}
          <CurriculumProgressTracker
            completedCount={currentProgressCount}
            titleClassName={isQuizComplete ? undefined : 'justify-center'}
            topicId={topicId}
            totalCount={totalSubTopics}
          />
        </div>

        {canReTakeQuiz && (
          <div className="flex flex-shrink pb-8">
            <Button onClick={onClickGenerateNewPath}>
              <span className="text-xxs md:text-sm underline !font-light underline-offset-4">
                {t('makeNewMasteryTrack')}
              </span>
            </Button>
          </div>
        )}
      </div>
      {showMindDetailsOverlay && (
        <MindDetailsOverlay onDismiss={() => dispatch(setShowMindDetailsOverlayActionCreator(false))} />
      )}
    </div>
  )
}
